/**
 * useVersionUpdate - detect when a newer frontend has been deployed.
 *
 * Phase: SPA cache-busting (2026-05-28).
 *
 * Single-page apps fetch index.html exactly once per tab. A user who keeps a
 * tab open never re-requests it, so they stay on the bundle they booted with - 
 * indefinitely - even after a deploy. This hook closes that gap:
 *
 *   1. On mount, fetch /version.json (no-store) and record it as the "boot"
 *      version - the version this tab is actually running.
 *   2. Poll /version.json every `pollIntervalMs`, and also whenever the tab
 *      regains visibility (user switches back to it).
 *   3. When the deployed version differs from the boot version, a new deploy
 *      has landed -> expose `updateAvailable = true`.
 *
 * version.json is uploaded with Cache-Control: no-cache (see
 * scripts/deployment/deploy-frontend-*.py) and we also append a cache-busting
 * query param + `cache: 'no-store'`, so the poll always sees the live value.
 *
 * Failures (missing file, network blip) are swallowed - the next tick retries.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const VERSION_URL = '/version.json';
const DEFAULT_POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

async function fetchDeployedVersion() {
  const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`version fetch failed: ${res.status}`);
  }
  const data = await res.json();
  return data && typeof data.version === 'string' ? data.version : null;
}

/**
 * @param {{ pollIntervalMs?: number }} [opts]
 * @returns {{ updateAvailable: boolean }}
 */
export default function useVersionUpdate({ pollIntervalMs = DEFAULT_POLL_INTERVAL_MS } = {}) {
  const bootVersionRef = useRef(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const check = useCallback(async () => {
    let current;
    try {
      current = await fetchDeployedVersion();
    } catch {
      return; // transient - retry next tick
    }
    if (!current) return;

    // First successful read establishes the baseline for this tab.
    if (bootVersionRef.current === null) {
      bootVersionRef.current = current;
      return;
    }
    if (current !== bootVersionRef.current) {
      setUpdateAvailable(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) check();
    };

    run(); // establish boot version immediately

    const intervalId = setInterval(run, pollIntervalMs);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [check, pollIntervalMs]);

  return { updateAvailable };
}
